import { PowerOffice } from "@/types/general";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@redux/hooks";

export type PowerOfficeRecordImportEntity = "customers" | "suppliers";

export interface PowerOfficeRecordImportError {
    code?: number;
    message: string;
}

type PowerOfficeRecordImportBaseStatus = {
    companyId: number;
    completedAt: string;
    unread: boolean;
};

export type PowerOfficeRecordImportStatus =
    | (PowerOfficeRecordImportBaseStatus & {
          status: "success";
          result: PowerOffice.DTO.ImportResult;
      })
    | (PowerOfficeRecordImportBaseStatus & {
          status: "error";
          error: PowerOfficeRecordImportError;
      });

type PowerOfficeRecordImportState = {
    /**
     * This tracks whether an import is running right now, and if so, for which entity.
     * Possible values are "customers", "suppliers", or undefined.
     *
     * While it is set, the UI can disable other imports and show a spinner for the active entity.
     **/
    activeEntity?: PowerOfficeRecordImportEntity;

    /**
     * This stores the most recent completed status per entity, keyed by entity name.
     * ```typescript
     * {
     *   customers: { status: "success" | "error", ... },
     *   suppliers: { status: "success" | "error", ... }
     * }
     * ```
     */
    latestByEntity: Partial<Record<PowerOfficeRecordImportEntity, PowerOfficeRecordImportStatus>>;
};

const initialState: PowerOfficeRecordImportState = {
    activeEntity: undefined,
    latestByEntity: {},
};

export const powerOfficeRecordImportSlice = createSlice({
    name: "powerOfficeRecordImports",
    initialState,
    reducers: {
        startPowerOfficeRecordImport: (
            state,
            action: PayloadAction<PowerOfficeRecordImportEntity>
        ) => {
            state.activeEntity = action.payload;
        },

        finishPowerOfficeRecordImport: (
            state,
            action: PayloadAction<{
                entity: PowerOfficeRecordImportEntity;
                companyId: number;
                result: PowerOffice.DTO.ImportResult;
            }>
        ) => {
            state.activeEntity = undefined;
            state.latestByEntity[action.payload.entity] = {
                status: "success",
                companyId: action.payload.companyId,
                completedAt: new Date().toISOString(),
                unread: true,
                result: action.payload.result,
            };
        },

        failPowerOfficeRecordImport: (
            state,
            action: PayloadAction<{
                entity: PowerOfficeRecordImportEntity;
                companyId: number;
                error: PowerOfficeRecordImportError;
            }>
        ) => {
            state.activeEntity = undefined;
            state.latestByEntity[action.payload.entity] = {
                status: "error",
                companyId: action.payload.companyId,
                completedAt: new Date().toISOString(),
                unread: true,
                error: action.payload.error,
            };
        },

        markPowerOfficeRecordImportAsRead: (
            state,
            action: PayloadAction<PowerOfficeRecordImportEntity>
        ) => {
            const latestStatus = state.latestByEntity[action.payload];

            if (latestStatus) {
                latestStatus.unread = false;
            }
        },

        clearPowerOfficeRecordImportStatus: (
            state,
            action: PayloadAction<PowerOfficeRecordImportEntity>
        ) => {
            delete state.latestByEntity[action.payload];
        },

        resetPowerOfficeRecordImports: () => initialState,
    },
});

export const selectPowerOfficeRecordImportActiveEntity = (state: RootState) =>
    state.powerOfficeRecordImports.activeEntity;

export const selectIsPowerOfficeRecordImportActive = (state: RootState) =>
    !!selectPowerOfficeRecordImportActiveEntity(state);

export const selectPowerOfficeRecordImportStatus =
    (entity: PowerOfficeRecordImportEntity) => (state: RootState) => {
        const latestStatus = state.powerOfficeRecordImports.latestByEntity[entity];
        const activeCompanyId = state.auth.user?.company?.id;

        if (!latestStatus || latestStatus.companyId !== activeCompanyId) {
            return undefined;
        }

        return latestStatus;
    };

export const {
    startPowerOfficeRecordImport,
    finishPowerOfficeRecordImport,
    failPowerOfficeRecordImport,
    markPowerOfficeRecordImportAsRead,
    clearPowerOfficeRecordImportStatus,
    resetPowerOfficeRecordImports,
} = powerOfficeRecordImportSlice.actions;

export default powerOfficeRecordImportSlice.reducer;
